import React, {useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	CircularProgress,
	Chip,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Tabs,
	Tab,
	Paper,
} from '@material-ui/core';
import {AccountBalanceWallet, Add} from '@material-ui/icons';
import {useOrders} from '../context/OrdersContext';
import API from '../../../../network/dashboardAPI';
import useAlert from '../../../../services/useAlert';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
	dialog: {
		'& .MuiDialog-paper': {
			minWidth: 700,
			maxHeight: '80vh',
		},
	},
	dialogTitle: {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	orderInfo: {
		padding: theme.spacing(2),
		backgroundColor: '#f5f7fa',
		borderRadius: 8,
		marginBottom: theme.spacing(2),
	},
	tableContainer: {
		maxHeight: 400,
	},
	form: {
		'& > *': {
			marginBottom: theme.spacing(2),
		},
	},
	amountDisplay: {
		fontSize: 32,
		fontWeight: 700,
		color: theme.palette.primary.main,
	},
}));

const WalletDialog = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const alert = useAlert();
	const {walletDialogOpen, selectedOrder} = state;

	const [tabValue, setTabValue] = useState(0);
	const [loading, setLoading] = useState(false);
	const [transactions, setTransactions] = useState([]);
	const [walletBalance, setWalletBalance] = useState(0);

	// Add transaction form
	const [transactionType, setTransactionType] = useState('credit');
	const [amount, setAmount] = useState('');
	const [reason, setReason] = useState('refund');
	const [comment, setComment] = useState('');
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (walletDialogOpen && selectedOrder) {
			fetchWalletTransactions();
		}
	}, [walletDialogOpen, selectedOrder]);

	const fetchWalletTransactions = async () => {
		if (!selectedOrder?.cust_id) return;

		setLoading(true);
		try {
			const response = await API.getWalletTransactionByCustomerId({
				customerId: selectedOrder.cust_id,
				next: 0,
				limit: 10,
			}).then(res => res.json());

			if (response.success) {
				setTransactions(response.data.list || []);
				// Get balance from customer_detail.wallet.wallet_amount
				const balance = selectedOrder?.customer_detail?.wallet?.wallet_amount || 0;
				setWalletBalance(balance);
			}
		} catch (error) {
			console.error('Failed to fetch wallet transactions:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleAddTransaction = async () => {
		if (!amount || parseFloat(amount) <= 0) {
			alert.warning('Warning', 'Please enter a valid amount');
			return;
		}

		setSubmitting(true);
		try {
			const body = {
				transaction_type: transactionType,
				wallet_owner_id: selectedOrder.cust_id,
				reason: reason,
				wallet_amount: Number(amount),
				comment: comment,
				order_number: selectedOrder.order_id,
			};

			const response = await API.addWalletTransaction(body)
				.then(res => res.json());

			if (response.success) {
				alert.success('Success', 'Transaction added successfully!');
				// Close dialog and refresh orders list
				handleClose();
				setTimeout(() => {
					actions.refreshData();
				}, 1000);
			} else {
				alert.error('Error', response.message || 'Failed to add transaction');
			}
		} catch (error) {
			alert.error('Error', 'Error: ' + error.message);
		} finally {
			setSubmitting(false);
		}
	};

	const handleClose = () => {
		setTabValue(0);
		setAmount('');
		setComment('');
		setTransactionType('credit');
		setReason('refund');
		actions.closeWalletDialog();
	};

	if (!selectedOrder) return null;

	return (
		<Dialog
			open={walletDialogOpen}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			className={classes.dialog}
		>
			<DialogTitle className={classes.dialogTitle}>
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Box display="flex" alignItems="center" gap={1}>
						<AccountBalanceWallet color="primary"/>
						<Typography variant="h6">Wallet Transactions</Typography>
					</Box>
					<Box textAlign="right">
						<Typography variant="caption" color="textSecondary">
							Current Balance
						</Typography>
						<Typography className={classes.amountDisplay}>
							${walletBalance.toFixed(2)}
						</Typography>
					</Box>
				</Box>
			</DialogTitle>

			<DialogContent style={{paddingTop: 0}}>
				{/* Order Info */}
				<Box className={classes.orderInfo}>
					<Typography variant="subtitle2" color="textSecondary">
						Customer Details
					</Typography>
					<Typography variant="h6">
						{selectedOrder.customer_detail?.name || 'N/A'}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						Order #{selectedOrder.order_id}
					</Typography>
				</Box>

				{/* Tabs */}
				<Paper>
					<Tabs
						value={tabValue}
						onChange={(e, newValue) => setTabValue(newValue)}
						indicatorColor="primary"
						textColor="primary"
					>
						<Tab label="Transaction History"/>
						<Tab label="Add Transaction"/>
					</Tabs>
				</Paper>

				{/* Tab Content */}
				{tabValue === 0 && (
					<Box mt={2}>
						{loading ? (
							<Box display="flex" justifyContent="center" padding={4}>
								<CircularProgress/>
							</Box>
						) : transactions.length === 0 ? (
							<Typography variant="body2" color="textSecondary" align="center" style={{padding: 32}}>
								No transactions found
							</Typography>
						) : (
							<TableContainer className={classes.tableContainer}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Admin</TableCell>
											<TableCell>Type</TableCell>
											<TableCell>Reason</TableCell>
											<TableCell>Date</TableCell>
											<TableCell align="right">Amount</TableCell>
											<TableCell>Comment</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{transactions.map((transaction, index) => (
											<TableRow key={index}>
												<TableCell>{transaction.admin_info || 'N/A'}</TableCell>
												<TableCell>
													<Chip
														label={transaction.transaction_type}
														size="small"
														color={transaction.transaction_type === 'credit' ? 'primary' : 'secondary'}
													/>
												</TableCell>
												<TableCell style={{textTransform: 'capitalize'}}>
													{transaction.reason || 'N/A'}
												</TableCell>
												<TableCell>
													{moment(transaction.createdAt).format('MMM DD, YYYY HH:mm')}
												</TableCell>
												<TableCell align="right">
													<Typography
														style={{
															color: transaction.transaction_type === 'credit' ? '#4caf50' : '#f44336',
															fontWeight: 600,
														}}
													>
														{transaction.transaction_type === 'credit' ? '+' : '-'}$
														{parseFloat(transaction.wallet_amount || 0).toFixed(2)}
													</Typography>
												</TableCell>
												<TableCell>{transaction.comment || '-'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Box>
				)}

				{tabValue === 1 && (
					<Box mt={2} className={classes.form}>
						<FormControl fullWidth variant="outlined" size="small">
							<InputLabel>Transaction Type</InputLabel>
							<Select
								value={transactionType}
								onChange={(e) => setTransactionType(e.target.value)}
								label="Transaction Type"
							>
								<MenuItem value="credit">Credit (Add Money)</MenuItem>
								<MenuItem value="debit">Debit (Deduct Money)</MenuItem>
							</Select>
						</FormControl>

						<TextField
							fullWidth
							label="Amount"
							type="number"
							variant="outlined"
							size="small"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							InputProps={{
								startAdornment: <Typography style={{marginRight: 8}}>$</Typography>,
							}}
						/>

						<FormControl fullWidth variant="outlined" size="small">
							<InputLabel>Reason</InputLabel>
							<Select
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								label="Reason"
							>
								<MenuItem value="marketing">Marketing</MenuItem>
								<MenuItem value="order">Order</MenuItem>
								<MenuItem value="refund">Refund</MenuItem>
								<MenuItem value="other">Other</MenuItem>
							</Select>
						</FormControl>

						<TextField
							fullWidth
							label="Comment"
							multiline
							rows={3}
							variant="outlined"
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							placeholder="Add any additional notes..."
						/>
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose} disabled={submitting}>
					Close
				</Button>
				{tabValue === 1 && (
					<Button
						onClick={handleAddTransaction}
						color="primary"
						variant="contained"
						disabled={submitting || !amount}
						startIcon={submitting ? <CircularProgress size={20}/> : <Add/>}
					>
						{submitting ? 'Adding...' : 'Add Transaction'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default WalletDialog;
